import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import copy from "copy-to-clipboard";
import { toast } from 'react-toastify';

import { ReactComponent as IconCopy } from "assets/images/icons/project-icons/copy.svg";
import { ReactComponent as IconLeft } from "assets/images/icons/project-icons/arrow-left.svg";
import { ReactComponent as IconEye } from "assets/images/icons/project-icons/eye.svg";
import { ReactComponent as IconFund } from "assets/images/icons/fund.svg";
import { ReactComponent as IconStatement} from "assets/images/icons/statement.svg";
import { ReactComponent as IconInfo} from "assets/images/icons/information-outline.svg";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import { ReactComponent as IconverticalDot } from "assets/images/icons/vertical-dot.svg";
import { ReactComponent as IconEyeSlash } from "assets/images/icons/project-icons/eye-slash-dark.svg";
import * as walletAction from 'store/entities/wallet/action'
import * as notificationAction from 'store/entities/notification/action'
import ContentLoading from '../common/ContentLoading';
import Maybe from '../common/Maybe';
import CurrencyFormat, { CurrencyFormatWithoutSymbol } from 'utils/currencyFormat';
import SearchBox from '../common/SearchBox';
import Filter from '../common/Filter';
import Table from '../common/Table';
import { TableContent } from '../common/TableContent';
import DateFormat, { TimeFormat } from 'utils/dateFormat';
import { renderStatusColor } from 'utils/statusColor';
import pluralize from 'utils/pluralize';
import Pagination from '../common/pagination';
import { ReactComponent as IconDebit } from "assets/images/icons/project-icons/arrow-up-right.svg";
import { ReactComponent as IconCredit } from "assets/images/icons/project-icons/arrow-down-left.svg";
import { ReactComponent as IconBank } from "assets/images/icons/project-icons/bank.svg";
import { ReactComponent as IconCard } from "assets/images/icons/project-icons/card-3.svg";
import { ReactComponent as IconChevronLeft } from "assets/images/icons/project-icons/chevron-left.svg";
import FormDrawer from '../common/form-drawer';
import { ButtonLoader, Input } from '../common/form';
import AccountInfo from './AccountInfo';
import GenerateAccountStatement from './GenerateAccountStatement';
import DropDown from './DropDown';
import FreezeAccount from './FreezeAccount';
import CloseAccount from './CloseAccount';
import UnfreezeAccount from './UnfreezeAccount';
import AccountSetting from './AccountSetting';
import MoveMoneyBetweenAccounts from './MoveMoneyBetweenAccounts';

const ViewSingleAccount = () => {
    const { walletId } = useParams();
    const dispatch = useDispatch();
    const history = useHistory();
    const {
        accountDetails,
        accountDetailsLoading,
        accountTransactions,
        accountTransactionsLoading,
    } = useSelector((s) => s.entities.wallet)
    const { data } = accountDetails;
    const [isOpenFundAccount, setIsOpenFundAccount] = useState(false);
    const [isOpenGenerateAccountStatement, setIsOpenGenerateAccountStatement] = useState(false);
    const [isOpenAccountSetting, setIsOpenAccountSetting] = useState(false);
    const [isOpenFreezeAccount, setIsOpenFreezeAccount] = useState(false);
    const [isOpenUnFreezeAccount, setIsOpenUnFreezeAccount] = useState(false);
    const [isOpenCloseAccount, setIsOpenCloseAccount] = useState(false);
    const [isOpenMoveMoneyBetweenAccounts, setIsOpenMoveMoneyBetweenAccounts] = useState(false);

    const getAccountDetails = () => {
        dispatch(walletAction?.getAccountDetails(String(walletId)));
        dispatch(walletAction?.getAccountTransactions(String(walletId)));
        dispatch(walletAction?.getBusinessAccounts());
    }

    const copyText = (textToCopy) => {
        let content = data;
        if (textToCopy){
            content = textToCopy;
        }
        copy(content);
        toast.success("Copied: " + content);
    }

    useEffect(() => {
        if (walletId) {
            getAccountDetails();
        }
    }, []);
    
    useEffect(() => {
        dispatch(walletAction?.resetMoveMoney());
    }, [dispatch])

  return (
    <div>
        <FundSingleWalletAccount
            isOpen={isOpenFundAccount}
            setIsOpen={setIsOpenFundAccount}
            accountDetailsLoading={accountDetailsLoading}
            accountDetails={accountDetails}
        />

        <FreezeAccount
            isOpen={isOpenFreezeAccount}
            setIsOpen={setIsOpenFreezeAccount}
        />

        <UnfreezeAccount
            isOpen={isOpenUnFreezeAccount}
            setIsOpen={setIsOpenUnFreezeAccount}
        />

        <CloseAccount
            isOpen={isOpenCloseAccount}
            setIsOpen={setIsOpenCloseAccount}
        />

        <AccountSetting
            isOpen={isOpenAccountSetting}
            setIsOpen={setIsOpenAccountSetting}
            accountDetailsLoading={accountDetailsLoading}
            accountDetails={accountDetails}
        />

        <MoveMoneyBetweenAccounts
            isOpen={isOpenMoveMoneyBetweenAccounts}
            setIsOpen={setIsOpenMoveMoneyBetweenAccounts}
            accountDetailsLoading={accountDetailsLoading}
            accountDetails={accountDetails?.data}
        />

        <GenerateAccountStatement
            isOpen={isOpenGenerateAccountStatement}
            setIsOpen={setIsOpenGenerateAccountStatement}
        />

        <div className="mt-3">
            <div className="w-full">
                <div className="page-title flex items-center">
                    <div className='bg-[#ECECEC] rounded-lg p-3 cursor-pointer'>
                        <IconLeft className="text-2xl " onClick={() => history.push("/account/all-accounts")} />
                    </div>
                    <span className="font-bold ml-3 text-[#0D0C2B] flex items-center">
                        {data?.accountName} {data?.isPrimaryAccount === true && (<span className='ml-5 font-normal bg-[#00535C1A] text-ep-primary-600 text-sm rounded-2xl px-2 py-1'>Primary</span>) }
                    </span>
                </div>
            </div>

            <div className='bg-ep-white rounded-2xl my-5 p-7 w-full'>
                <div className='flex flex-col lg:flex-row justify-between gap-6'>
                    <div className='w-full sm:w-2/4 lg:pr-6 lg:border-r lg:border-ep-gray-800'>
                        <div className='mb-3'>
                            Available Balance
                        </div>
                        <div className='font-bold text-3xl mb-7'>
                            <div className="mt-2 flex items-center">
                                <AccountBalance
                                    loading={accountDetailsLoading}
                                    balance={data?.balance}
                                />
                            </div>
                        </div>

                        <div className='flex space-x-4 flex-wrap sm:flex-nowrap'>
                            <button onClick={() => setIsOpenMoveMoneyBetweenAccounts((prevState) => !prevState)}
                                disabled={accountDetails?.data?.accountStatus.toLowerCase() === 'frozen' || accountDetails?.data?.balance === 0}
                                className={`btn btn-lg !px-5 mt-3 whitespace-nowrap bg-ep-primary-600 text-white ${accountDetails?.data?.accountStatus.toLowerCase() === "frozen" ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
                            >
                                Move money
                            </button>

                            {/* If it's a collection account */}
                            {accountDetails?.data?.walletType?.toLowerCase() !== "collection" ? (
                                <button onClick={() => setIsOpenFundAccount(true)}
                                    disabled={accountDetails?.data?.accountStatus.toLowerCase() === 'frozen'}
                                    className={`btn btn-lg text-[#193F52] !px-5 mt-3 whitespace-nowrap bg-ep-primary-600 bg-opacity-10 ${accountDetails?.data?.accountStatus.toLowerCase() === "frozen" ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}>
                                    <IconFund className="mr-2" />
                                    Fund
                                </button>) : null
                            }

                            <button onClick={() => setIsOpenGenerateAccountStatement(true)} type="button" className="btn btn-lg !px-5 mt-3 whitespace-nowrap text-ep-primary-600 bg-ep-primary-600 bg-opacity-10">
                               <IconStatement className="mr-2" />
                                Generate statement
                            </button>

                            {/* If not a primary account and it's an expense account */}
                            <Maybe condition={accountDetails?.data?.isPrimaryAccount === false}>
                                <Maybe condition={accountDetails?.data?.walletType === "expense"}>
                                    <button className="">
                                        <DropDown titleIcon={<IconverticalDot className='h-6' />} listClass="flex gap-2 item-center p-4 w-[200px] cursor-pointer" triggerClass="btn btn-lg !px-5 mt-3 whitespace-nowrap bg-ep-primary-600 bg-opacity-10" dropdownArray={[
                                            {
                                                title: `${data?.accountStatus.toLowerCase() === "frozen" ? "Unfreeze" : "Freeze"}`,
                                                func: () => `${data?.accountStatus.toLowerCase() === "frozen" ? setIsOpenUnFreezeAccount((prevState) => !prevState) : setIsOpenFreezeAccount((prevState) => !prevState)}`,
                                                isFreeze: false
                                            },
                                            {
                                                title: "Close Account",
                                                func: () => setIsOpenCloseAccount((prevState) => !prevState),
                                                color: 'text-[#DC2626]',
                                                isFreeze: false
                                            }
                                        ]}/>
                                    </button>
                                </Maybe>
                            </Maybe>

                            {/* If not a primary account and it's a collection account */}
                            <Maybe condition={accountDetails?.data?.isPrimaryAccount === false && accountDetails?.data?.walletType === "collection"}>
                                <button className="">
                                    <DropDown titleIcon={<IconverticalDot className='h-6' />} listClass="flex gap-2 item-center p-4 w-[200px] cursor-pointer" triggerClass="btn btn-lg !px-5 mt-3 whitespace-nowrap bg-ep-primary-600 bg-opacity-10" dropdownArray={[
                                        {
                                            title: "Close Account",
                                            func: () => setIsOpenCloseAccount((prevState) => !prevState),
                                            color: 'text-[#DC2626]',
                                            isFreeze: false
                                        }
                                    ]}/>
                                </button>
                            </Maybe>

                        </div>
                    </div>

                    <div className='w-full sm:w-2/4'>
                        <div className='mb-5'>
                            <div className='text-[#717171] mb-3'>Account Number</div>
                            <div className='text-[#1D1D1D] font-medium text-2xl flex items-center'>
                                <ContentLoading
                                    loading={accountDetailsLoading}
                                    data={<>
                                        {data?.accountNumber ? (
                                            <>
                                                {data?.accountNumber}
                                                <IconCopy onClick={() => copyText(data?.accountNumber)} className="text-ep-primary-600 ml-3 cursor-pointer text-xl" />
                                            </>
                                            ) : "N/A"
                                        }
                                    </>}
                                />
                            </div>
                        </div>
                        <div className='flex items-center gap-6'>
                            <div>
                                <div className='text-[#717171] mb-3'>Bank Name</div>
                                <div className='text-[#1D1D1D] font-medium capitalize'>
                                    <ContentLoading
                                        loading={accountDetailsLoading}
                                        data={<>
                                            {data?.bankName || "N/A"}
                                        </>}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className='text-[#717171] mb-3 flex items-center'>
                                    Account Type
                                    <span className='ml-2'>
                                        <IconInfo data-tip data-for="account-type" className='w-5 h-5 cursor-pointer'/>
                                        <ReactTooltip id="account-type" place="top" effect="solid" className='custom-tooltip'>
                                            <p className='font-bold'>{data?.companyAccountType?.toLowerCase() === "expense" || data?.walletType?.toLowerCase() === "expense" ? "Expense Account" : "Collection Account"}:</p>
                                            {data?.walletType?.toLowerCase() === "expense" || data?.companyAccountType?.toLowerCase() === "expense" ?
                                                <p>This account allows you to receive and <br /> make payments.</p> :
                                                    <p>This account allows you to only receive <br /> money and move to the main account</p>}
                                        </ReactTooltip>
                                    </span>
                                </div>
                                <div className='text-[#1D1D1D] font-medium capitalize'>
                                    <ContentLoading
                                        loading={accountDetailsLoading}
                                        data={<>
                                            {data?.companyAccountType || data?.walletType ? `${data?.companyAccountType || data?.walletType} Account` : "N/A"}
                                        </>}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 box">
                <div className="flex items-center justify-start w-max space-x-3">
                    <div className="font-medium text-xl">All Transactions</div>
                    <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                        <ContentLoading
                            loading={accountTransactionsLoading}
                            data={<>{accountTransactions?.data?.totalDocs ?? "-"} {pluralize(accountTransactions?.data?.totalDocs, "transaction", "transactions")}</>}
                        />
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap justify-between">
                        <>
                            <div className="max-w-sm flex-grow sm:mr-6">
                                <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                            </div>
                            <div className="flex flex-wrap space-x-3 sm:space-x-3">
                                <div>
                                    <Filter filters={[
                                        {
                                            title: "Custom Date Range",
                                            dataType: "date-range",
                                            options: {
                                                from: {
                                                    name: "startDate",
                                                    placeholder: "Start Date",
                                                },
                                                to: {
                                                    name: "endDate",
                                                    placeholder: "End Date",
                                                },
                                            },
                                        },
                                        {
                                            title: "Category",
                                            name: "category",
                                            dataType: "select",
                                            options: [
                                                {
                                                    display: "All",
                                                    payload: "",
                                                },
                                                {
                                                    display: "Transfer",
                                                    payload: "transafer",
                                                },
                                                {
                                                    display: "Airtime",
                                                    payload: "airtime",
                                                },
                                                {
                                                    display: "Scheduled Transfer",
                                                    payload: "scheduled-transfer",
                                                },
                                                {
                                                    display: "Payroll",
                                                    payload: "payroll",
                                                },
                                            ],
                                        },
                                        {
                                            title: "Transaction Status",
                                            name: "status",
                                            dataType: "select",
                                            options: [
                                                {
                                                    display: "All",
                                                    payload: "",
                                                },
                                                {
                                                    display: "Successful",
                                                    payload: "successful",
                                                },
                                                {
                                                    display: "Pending",
                                                    payload: "pending",
                                                },
                                                {
                                                    display: "Failed",
                                                    payload: "failed",
                                                },
                                                {
                                                    display: "Reversed",
                                                    payload: "reversed",
                                                },
                                            ],
                                        },
                                        {
                                            title: "Type",
                                            name: "type",
                                            dataType: "select",
                                            options: [
                                                {
                                                    display: "All",
                                                    payload: "",
                                                },
                                                {
                                                    display: "Credit",
                                                    payload: "credit",
                                                },
                                                {
                                                    display: "Debit",
                                                    payload: "debit"
                                                }
                                            ],
                                        },
                                    ]}/>
                                </div>
                            </div>
                        </>
                </div>
            </div>

            <div className="bg-white">

                    <Table classTable="table-hover">
                        <Table.Head>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Transaction</th>
                            {/* <th>Narration</th> */}
                            <th>Amount</th>
                            <th>Status</th>
                        </Table.Head>
                        <Table.Body>

                            <TableContent
                                loading={accountTransactionsLoading}
                                loadingText="Loading account transactions"
                                data={accountTransactions?.data}
                                colspan={6}
                                emptyUI={<>
                                    <div className="py-20 mx-auto text-center text-base">
                                        <div className="flex justify-center">
                                            <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                        </div>
                                        <div className="text-[#101828] font-medium">
                                            No Data Available Yet
                                        </div>
                                        <div className="mt-1 text-[#4A5564]">
                                            Once you start transacting, it will appear here
                                        </div>
                                    </div>
                                </>}
                            />

                            {accountTransactionsLoading === false && accountTransactions?.data?.docs?.map((transaction, index) => {
                                const isDebit = transaction?.type === 'debit'

                                return (
                                    <tr key={index}>
                                        <td style={{ width: '180px' }}>
                                            <div className="py-2.5">
                                                <div className="text-[#667085]">
                                                    {DateFormat(transaction?.createdAt)} at {TimeFormat(transaction?.createdAt)}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="capitalize flex items-center">
                                                <div className={`w-8 h-8 mr-3 flex rounded-full flex-shrink-0 ${isDebit ? "bg-[#FEF2F2]" : "bg-[#cfffe3]"}`}>
                                                    {isDebit ? <IconDebit className="m-auto" /> : <IconCredit className="m-auto" />}
                                                </div>
                                                {transaction?.type}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="mb-1 text-[#101828]">{transaction?.description}</div>
                                        </td>
                                        {/* <td className="capitalize text-[#667085]">
                                            {transaction?.narration || "N/A"}
                                        </td> */}
                                        <td className="!text-[#101828] font-normal">
                                            {CurrencyFormat(transaction?.amount / 100)}
                                        </td>
                                        <td>
                                            <div className={"label capitalize " + renderStatusColor(transaction?.status)}>
                                                {transaction?.status}
                                            </div>
                                        </td>
                                </tr>
                                )
                            })}

                        </Table.Body>
                    </Table>

                    <Maybe condition={accountTransactionsLoading === false && accountTransactions?.data?.docs?.length > 0}>
                        <div className="mt-6">
                            <Pagination data={accountTransactions?.data} />
                        </div>
                    </Maybe>
            </div>
        </div>
    </div>
  )
}

const AccountBalance = (props) => {
    const {loading, balance} = props;
    const [isBalanceVisible, setIsBalanceVisible] = useState(true);

    return (
        <>
            <div className="text-3xl font-semibold mr-6">
                <Maybe condition={isBalanceVisible === true}>
                    <ContentLoading
                        loading={loading}
                        data={<>
                            {CurrencyFormat(balance / 100 || 0, true)}
                        </>}
                    />
                </Maybe>
                <Maybe condition={isBalanceVisible === false}>
                    ••••
                </Maybe>
            </div>
            <div onClick={() => setIsBalanceVisible(!isBalanceVisible)} className="w-7 h-7 rounded-lg cursor-pointer">
                <Maybe condition={isBalanceVisible === true}>
                    <IconEyeSlash className="w-full h-full object-contain svg-stroke-black" />
                </Maybe>
                <Maybe condition={isBalanceVisible === false}>
                    <IconEye className="w-full h-full object-contain svg-stroke-black" />
                </Maybe>
            </div>
        </>
    )
}

const FundSingleWalletAccount = (props) => {
    const dispatch = useDispatch();
    const { fundBusinessAccountLoading, fundBusinessAccount: fundBusinessAccountData } = useSelector((state) => state?.entities?.wallet);
    const {isOpen, setIsOpen, accountDetailsLoading, accountDetails} = props;
    const { data } = accountDetails;
    const [isOpenAmount, setIsOpenAmount] = useState(false);
    const formDefault = {
        amount: "",
    }
    const [form, setForm] = useState(formDefault)
    
    const copyText = () => {
        let content = data;
        if (data?.bankName){
            content = data?.bankName ?? "";
        }
        copy(content);
        toast.success("Copied: " + content);
    }

    const handleFundBusinessAccount = () => {
        setIsOpen(false);
        setIsOpenAmount(true);
    }

    const submitForm = (e) => {
        // Send walletId and amount
        e.preventDefault();
        const payload = {
            walletId: String(data?.walletId),
            amount: Number(form?.amount?.split(",").join(""))
        }

        dispatch(walletAction.fundBusinessAccount(payload));
        dispatch(notificationAction.getAllNotifications());
    }

    // Redirect the business once payment link is available
    useEffect(() => {
        if (fundBusinessAccountData?.data?.paymentLinkUrl) {
            window.location.href = fundBusinessAccountData?.data?.paymentLinkUrl;
        }
    }, [fundBusinessAccountData]);

    return (
        <>
            <Maybe condition={isOpenAmount === true}>
                <FormDrawer display="center" size="lg" isOpen={isOpenAmount} setIsOpen={setIsOpenAmount}>
                    <div className="p-6">
                        <FormDrawer.Title
                            title="Enter Amount"
                            showCloseButton={true}
                            close={setIsOpenAmount}
                        />

                        <hr className="w-full h-px mt-2 bg-[#E2E4E8] border-none" />
                        <form onSubmit={submitForm} className="mt-8">
                            <Input
                                type="text"
                                label="Amount (Naira)"
                                placeholder="0.00"
                                inputClass={"pl-4"}
                                value={form?.amount}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    const cleanedValue = value.replace(/[^\d.]/g, '');
                                    const [integerPart, decimalPart] = cleanedValue.split('.');
                                    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                    let formattedValue = formattedInteger;
                                    if (decimalPart !== undefined) {
                                        formattedValue += '.' + decimalPart.slice(0, 2);
                                    }
                                    setForm({
                                        ...form, 
                                        amount: formattedValue,
                                    })
                                }}
                            />
                            <div className="mt-10 flex justify-end space-x-4">
                                <button type="button" onClick={() => setIsOpenAmount(false)} className="btn btn-lg btn-transparent-primary">
                                    Cancel
                                </button>
                                <ButtonLoader type="submit" loading={fundBusinessAccountLoading} className="btn btn-lg btn-ep-primary">
                                    Continue
                                </ButtonLoader>
                            </div>
                        </form>
                    </div>
                </FormDrawer>
            </Maybe>

            {/* Business Account Details */}
            <FormDrawer display="center" size="lg" isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="p-6">
                    <FormDrawer.Title
                        title="Fund Account"
                        description="Add money to your business account"
                        showCloseButton={true}
                        close={setIsOpen}
                    />

                    <hr className="w-full h-px mt-2 bg-[#E2E4E8] border-none" />

                    <div className="p-4 mt-4 bg-ep-primary-light-100 rounded-lg">
                        <div className="flex items-center">
                            <div className="w-12 h-12 mr-4 flex flex-shrink-0 border border-[#D1D5DB] rounded-full">
                                <IconBank className="m-auto" />
                            </div>
                            <div className="flex-grow">
                                <div className="font-medium">
                                    Bank Transfer
                                </div>
                                <div className="text-[#27272E] text-sm">
                                    Transfer to the bank account below
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="mt-6 flex flex-col flex-1 rounded-xl border p-3 bg-[#FFFFFF] space-y-4">
                                <AccountInfo
                                    loading={accountDetailsLoading}
                                    label="Account Number"
                                    data={data?.accountNumber ?? "-"}
                                    showCopyIcon={true}
                                />
                                <AccountInfo
                                    loading={accountDetailsLoading}
                                    label="Account Name"
                                    data={data?.accountName ?? "-"}
                                />
                                <AccountInfo
                                    loading={accountDetailsLoading}
                                    label="Bank Name"
                                    data={<span className="capitalize">{data?.bankName ?? "-"}</span>}
                                    textToCopy={data?.bankName ?? ""}
                                />
                            </div>
                            {
                               data?.oldBankName && 
                                <div className="mt-6 flex flex-col flex-1 rounded-xl border p-3 space-y-4 bg-[#FFFFFF]">
                                    <div className="">
                                        <div className="text-[#717171] flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <p>Account Number</p>
                                                {data?.oldBankName && <p className="text-[#B42318] bg-[#FEF3F2] rounded-2xl py-0.5 px-2.5">Old Account</p>}
                                            </div>
                                            <Maybe condition={true}>
                                            <button type="button" className="w-5 h-5" data-tip data-for="old-account"><IconInfo /></button>
                                                <ReactTooltip id="old-account" place="top" effect="solid" className="custom-tooltip" offset={{ top: 10, left: 10 }}>
                                                    <p className="text-[14px]">
                                                        We have switched our accounts provider to Stanbic IBTC. Please <br /> 
                                                        note that Providus accounts will no longer be available from 1st <br />
                                                        September 2024. <br />
                                                        Kindly update all your financial workflows accordingly.
                                                    </p>
                                                </ReactTooltip>
                                            </Maybe>
                                        </div>
                                        <div className={`mt-0.5 font-medium flex items-center ${"Account Number" ? "text-2xl" : ""}`}>
                                            <ContentLoading
                                                loading={accountDetailsLoading}
                                                data={data?.oldAccountNumber}
                                            />
                                            <Maybe condition={accountDetailsLoading === false && data?.oldAccountNumber}>
                                                <Maybe condition={true}>
                                                    <IconCopy onClick={() => copyText(data?.oldAccountNumber)} className="text-ep-primary-600 ml-3 cursor-pointer w-6 h-6" />
                                                </Maybe>
                                            </Maybe>
                                        </div>
                                    </div>
                                    <AccountInfo
                                    loading={accountDetailsLoading}
                                    label="Account Name"
                                    data={data?.oldAccountName ?? "-"}
                                    />
                                    <AccountInfo
                                        loading={accountDetailsLoading}
                                        label="Bank Name"
                                        data={<span className="capitalize">{data?.oldBankName ?? "-"}</span>}
                                        textToCopy={data?.oldBankName ?? ""}
                                    />
                                </div>
                            }
                            
                        </div>
                    </div>

                    <div onClick={handleFundBusinessAccount} className="p-4 mt-4 flex items-center bg-ep-primary-light-100 rounded-lg cursor-pointer">
                        <div className="w-12 h-12 mr-4 flex flex-shrink-0 border border-[#D1D5DB] rounded-full">
                            <IconCard className="m-auto" />
                        </div>
                        <div className="flex-grow">
                            <div className="font-medium">
                                Credit or Debit Card
                            </div>
                            <div className="text-[#27272E] text-sm">
                                Add money using a debit or credit card
                            </div>
                        </div>
                        <div className="w-6 h-full flex-shrink-0 flex justify-end items-center">
                            <IconChevronLeft className="svg-fill-black" />
                        </div>
                    </div>
                </div>
            </FormDrawer>
        </>
    )
}

export default ViewSingleAccount;